import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, ArrowUp, ArrowDown, Minus, Download } from 'lucide-react';
import { T, RISK_COLORS, RISK_BG, STATUS_COLORS, fmtNum, healthColor, healthLabel } from '../theme';
import { HELP } from '../helpContent';

// ─── COMPANY BADGE (Option 2: Logo + Colored Ring) ───
const CO_META = {
  salam: { name: "Al Salam", color: "#d4a017", logo: "/logo-salam.png" },
  river: { name: "River Prime", color: "#3b82f6", logo: "/logo-river.png" },
};
export const CoBadge = ({ co, size = 18 }) => {
  if (!co || !CO_META[co]) return null;
  const m = CO_META[co];
  return (
    <span title={m.name} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%", border: `2px solid ${m.color}`,
      flexShrink: 0, overflow: "hidden", verticalAlign: "middle" }}>
      <img src={m.logo} alt={m.name} style={{ width: size - 5, height: size - 5, objectFit: "contain" }}
        onError={e => { e.target.style.display = "none"; e.target.parentElement.textContent = co === "salam" ? "S" : "R"; }} />
    </span>
  );
};

// ─── BADGES ───
export const Badge = ({ text, label, color, bg }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
    color: color || T.cyan, background: bg || `${color || T.cyan}18`, whiteSpace: "nowrap", letterSpacing: 0.5 }}>
    {text || label || "—"}
  </span>
);
export const RiskBadge = ({ level }) => <Badge text={level || "—"} color={RISK_COLORS[level] || T.textMuted} bg={RISK_BG[level] || "transparent"} />;
export const StatusBadge = ({ status }) => <Badge text={status} color={STATUS_COLORS[status] || T.textMuted} bg={`${STATUS_COLORS[status] || T.textMuted}20`} />;
export const HealthBadge = ({ score }) => {
  const c = healthColor(score);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, background: `${c}18`, color: c, fontSize: 11, fontWeight: 600 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
      {score} — {healthLabel(score)}
    </span>
  );
};

// ─── V2-STYLE STAT CARD (used for everything) ───
export const StatCard = ({ icon, label, value, sub, color = T.cyan, trend, trendLabel, delay = 0 }) => {
  // icon can be: React element (<Users size={20} />) OR component (Users)
  const IconEl = typeof icon === 'function' ? icon : null;
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: "18px 20px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, color: T.text }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: T.textDim, marginTop: 4 }}>{sub}</div>}
          {trend != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 11,
              color: trend > 0 ? T.green : trend < 0 ? T.red : T.textDim }}>
              {trend >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {Math.abs(trend).toFixed(1)}% {trendLabel || "vs prev"}
            </div>
          )}
        </div>
        {icon && (
          <div style={{ color, opacity: 0.6 }}>
            {IconEl ? <IconEl size={22} color={color} /> : icon}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MINI BAR ───
export const MiniBar = ({ value, max = 100, color = T.cyan }) => (
  <div style={{ width: 80, height: 6, background: T.bgHover || T.border, borderRadius: 3, overflow: "hidden" }}>
    <div style={{ width: `${Math.min((value / (max || 1)) * 100, 100)}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.5s" }} />
  </div>
);

// ─── HEALTH BAR ───
export const HealthBar = ({ score }) => {
  const c = healthColor(score);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 60, height: 6, background: T.bgHover || T.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: score + "%", background: c, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: c }}>{score}</span>
    </div>
  );
};

// ─── CHART TOOLTIP ───
export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
      <div style={{ color: T.textDim, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: "flex", gap: 8, justifyContent: "space-between" }}>
          <span>{p.name}:</span>
          <span className="mono" style={{ fontWeight: 600 }}>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── DATA TABLE (supports both V1 key-based and V2 render-based columns) ───
// ── CSV Export helper ──
const extractText = (node) => {
  if (node == null || node === "NULL") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (typeof node === "boolean") return node ? "Yes" : "No";
  if (node?.props) {
    const { children, text, label, value } = node.props;
    if (text) return String(text);
    if (label) return String(label);
    if (value != null) return String(value);
    if (children) {
      if (typeof children === "string" || typeof children === "number") return String(children);
      if (Array.isArray(children)) return children.map(extractText).filter(Boolean).join(" ");
      return extractText(children);
    }
  }
  return String(node);
};

export const exportTableCSV = (columns, sorted, title) => {
  const exportCols = columns.filter(c => c.key || c.value || c.render);
  const header = exportCols.map(c => c.label || "").join(",");
  const rows = sorted.map(row => exportCols.map(c => {
    let val;
    if (c.exportValue) val = c.exportValue(row);
    else if (c.value) val = c.value(row);
    else if (c.key) val = row[c.key];
    if ((val == null || val === "NULL" || val === "") && c.render) {
      const rendered = c.render(c.key ? row[c.key] : row, row);
      val = extractText(rendered);
    }
    if (val == null || val === "NULL") val = "";
    val = String(val).replace(/,/g, ";").replace(/\n/g, " ").replace(/"/g, "'");
    return val;
  }).join(","));
  const csv = "\uFEFF" + [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${title || "export"}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

// Simple export for raw tables: pass headers[] and rows[][]
export const exportRawCSV = (headers, rows, title) => {
  const csv = "\uFEFF" + [headers.join(","), ...rows.map(r => r.map(v => {
    if (v == null) return "";
    return String(v).replace(/,/g, ";").replace(/\n/g, " ").replace(/"/g, "'");
  }).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${title || "export"}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

// Tiny export button for raw tables
export const ExportBtn = ({ onClick, count, style: s }) => (
  <button onClick={onClick} title={`Export ${count || ""} rows as CSV`}
    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 5, border: `1px solid ${T.cyan}30`,
      background: T.cyan + "10", color: T.cyan, fontSize: 10, fontWeight: 600, cursor: "pointer", ...s }}>
    <Download size={11} /> CSV
  </button>
);

export const DataTable = ({ columns, data, onRowClick, pageSize = 20, emptyMsg, fixedLayout, minWidth, compact, exportTitle }) => {
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [jumpInput, setJumpInput] = useState("");

  useEffect(() => setPage(0), [data]);

  const sorted = useMemo(() => {
    if (sortCol == null) return data;
    return [...data].sort((a, b) => {
      const col = columns[sortCol];
      let va, vb;
      if (col.sortFn) return sortDir === "asc" ? col.sortFn(a, b) : col.sortFn(b, a);
      if (col.value) { va = col.value(a); vb = col.value(b); }
      else if (col.key) { va = a[col.key]; vb = b[col.key]; }
      else return 0;
      if (va == null || va === "NULL") va = "";
      if (vb == null || vb === "NULL") vb = "";
      const na = Number(va), nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) return sortDir === "asc" ? na - nb : nb - na;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [data, sortCol, sortDir, columns]);

  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(sorted.length / pageSize);

  const handleExport = useCallback(() => exportTableCSV(columns, sorted, exportTitle), [columns, sorted, exportTitle]);

  // ── SUMMARY TOTALS for numeric columns ──
  const SKIP_SUM = new Set(["Login", "CU_Account", "CU", "Name", "Phone_Number", "IP", "Login_Same_IP", "CU_Same_IP", "RelatedAccounts", "Leverage", "MonthKey", "Registration", "LastAccess", "CRM_Account_Open_Date", "First_Deposit_Time", "LifetimeDays", "Deposit_Count", "IP_ODT", "healthScore"]);
  const summaryRow = useMemo(() => {
    if (!data.length) return null;
    const sumFmts = new Set(["money"]);
    const sums = {};
    let hasSummable = false;
    columns.forEach(c => {
      if (!c.key || SKIP_SUM.has(c.key)) { sums[c.key || c.label] = null; return; }
      // Only sum money/financial columns, or columns explicitly marked number with fmt
      const isMoney = c.fmt === "money" || (c.key && /Deposit|Withdraw|Profit|Equity|Credit|Floating|Volume|Swap|Commission|Gross|Net|Trade/i.test(c.key));
      if (!isMoney) { sums[c.key] = null; return; }
      hasSummable = true;
      sums[c.key] = data.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
    });
    if (!hasSummable) return null;
    return sums;
  }, [data, columns]);

  // ── COUNT STATS: unique CU + total Logins ──
  const countStats = useMemo(() => {
    if (!data.length) return null;
    const hasCU = columns.some(c => c.key === "CU_Account");
    const hasLogin = columns.some(c => c.key === "Login");
    if (!hasCU && !hasLogin) return null;
    const uniqueCU = hasCU ? new Set(data.map(r => r.CU_Account).filter(Boolean)).size : 0;
    const totalLogins = hasLogin ? data.length : 0;
    return { uniqueCU: hasCU ? uniqueCU : null, totalLogins: hasLogin ? totalLogins : null };
  }, [data, columns]);

  // ── SMART PAGINATION helpers ──
  const goPage = (p) => setPage(Math.max(0, Math.min(totalPages - 1, p)));
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const pages = [0];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages - 2, page + 2);
    if (page < 4) { start = 1; end = Math.min(5, totalPages - 2); }
    if (page > totalPages - 5) { start = Math.max(1, totalPages - 6); end = totalPages - 2; }
    if (start > 1) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 2) pages.push("...");
    pages.push(totalPages - 1);
    return pages;
  };

  const fmtSummary = (v) => {
    if (v == null) return "";
    const a = Math.abs(v);
    const s = a >= 1e9 ? (a / 1e9).toFixed(1) + "B" : a >= 1e6 ? (a / 1e6).toFixed(1) + "M" : a >= 1e3 ? (a / 1e3).toFixed(1) + "K" : a.toFixed(a % 1 === 0 ? 0 : 1);
    return v < 0 ? "-" + s : s;
  };

  return (
    <div>
      {/* ── SUMMARY TOTALS BAR ── */}
      {(summaryRow || countStats) && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          {countStats && countStats.uniqueCU != null && (
            <div style={{ padding: "6px 14px", borderRadius: 8, background: T.bgCard, border: "1px solid " + T.amber + "30", minWidth: 80 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 2 }}>Unique CU</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: T.amber }}>{countStats.uniqueCU.toLocaleString()}</div>
            </div>
          )}
          {countStats && countStats.totalLogins != null && (
            <div style={{ padding: "6px 14px", borderRadius: 8, background: T.bgCard, border: "1px solid " + T.cyan + "30", minWidth: 80 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 2 }}>Logins</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: T.cyan }}>{countStats.totalLogins.toLocaleString()}</div>
            </div>
          )}
          {summaryRow && columns.map((c, i) => {
            const key = c.key || c.label;
            const val = summaryRow[key];
            if (val == null) return null;
            const isMoney = c.fmt === "money" || (c.key && (c.key.includes("Deposit") || c.key.includes("Withdraw") || c.key.includes("Profit") || c.key.includes("Equity") || c.key.includes("Credit") || c.key.includes("Floating")));
            return (
              <div key={i} style={{ padding: "6px 14px", borderRadius: 8, background: T.bgCard, border: "1px solid " + T.border, minWidth: 80 }}>
                <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 2 }}>{c.label}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: isMoney ? (val >= 0 ? T.green : T.red) : T.cyan }}>
                  {isMoney ? (val < 0 ? "-" : "") + "$" + fmtSummary(Math.abs(val)) : fmtSummary(val)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${T.border}` }}>
        <table style={{ width: "100%", minWidth: minWidth || "auto", borderCollapse: "collapse", fontSize: compact ? 12 : 13, tableLayout: fixedLayout ? "fixed" : "auto" }}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i} onClick={() => { if (sortCol === i) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(i); setSortDir("asc"); } setPage(0); }}
                  style={{ padding: compact ? "8px 8px" : "10px 14px", textAlign: c.align || "left", background: T.bgTable, color: T.textDim,
                    fontWeight: 600, fontSize: compact ? 10 : 11, textTransform: "uppercase", letterSpacing: 0.8,
                    cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${T.border}`, userSelect: "none", width: c.width || "auto" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: c.align === "center" ? "center" : c.align === "right" ? "flex-end" : "flex-start" }}>
                    {c.label}
                    {sortCol === i && (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ padding: 30, textAlign: "center", color: T.textMuted }}>{emptyMsg || "No data"}</td></tr>
            ) : paged.map((row, i) => (
              <tr key={i} onClick={() => onRowClick?.(row)}
                style={{ cursor: onRowClick ? "pointer" : "default", background: i % 2 === 0 ? "transparent" : `${T.bgCard}60` }}
                onMouseEnter={e => e.currentTarget.style.background = T.cyanGlow}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : `${T.bgCard}60`}>
                {columns.map((c, ci) => (
                  <td key={ci} style={{ padding: compact ? "7px 8px" : "10px 14px", borderBottom: `1px solid ${T.border}20`, whiteSpace: "nowrap", textAlign: c.align || "left", width: c.width || "auto", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {c.render ? c.render(c.key ? row[c.key] : row, row) : (row[c.key] == null || row[c.key] === "NULL" ? "—" : String(row[c.key]))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ── Footer: results + export + smart pagination ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, padding: "0 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: T.textDim }}>{sorted.length} results</span>
          {sorted.length > 0 && (
            <button onClick={handleExport} title={`Export ${sorted.length} rows as CSV`}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 5, border: `1px solid ${T.cyan}30`,
                background: T.cyan + "10", color: T.cyan, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
              <Download size={11} /> CSV
            </button>
          )}
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* First + Prev */}
            <button onClick={() => goPage(0)} disabled={page === 0} title="First page"
              style={{ padding: "4px 8px", borderRadius: 5, border: "1px solid " + T.border, background: "transparent", color: page === 0 ? T.textMuted : T.text, cursor: page === 0 ? "default" : "pointer", fontSize: 10, fontWeight: 700 }}>«</button>
            <button onClick={() => goPage(page - 1)} disabled={page === 0}
              style={{ padding: "4px 8px", borderRadius: 5, border: "1px solid " + T.border, background: "transparent", color: page === 0 ? T.textMuted : T.text, cursor: page === 0 ? "default" : "pointer", fontSize: 11 }}>
              <ChevronLeft size={13} />
            </button>
            {/* Page numbers */}
            {getPageNumbers().map((p, i) =>
              p === "..." ? <span key={"e" + i} style={{ color: T.textMuted, fontSize: 11, padding: "0 2px" }}>…</span> : (
                <button key={p} onClick={() => goPage(p)}
                  style={{ padding: "4px 9px", borderRadius: 5, border: page === p ? "1px solid " + T.cyan : "1px solid " + T.border,
                    background: page === p ? T.cyanGlow : "transparent", color: page === p ? T.cyan : T.textDim,
                    cursor: "pointer", fontSize: 11, fontWeight: page === p ? 700 : 400, minWidth: 28, textAlign: "center" }}>{p + 1}</button>
              )
            )}
            {/* Next + Last */}
            <button onClick={() => goPage(page + 1)} disabled={page >= totalPages - 1}
              style={{ padding: "4px 8px", borderRadius: 5, border: "1px solid " + T.border, background: "transparent", color: page >= totalPages - 1 ? T.textMuted : T.text, cursor: page >= totalPages - 1 ? "default" : "pointer", fontSize: 11 }}>
              <ChevronRight size={13} />
            </button>
            <button onClick={() => goPage(totalPages - 1)} disabled={page >= totalPages - 1} title="Last page"
              style={{ padding: "4px 8px", borderRadius: 5, border: "1px solid " + T.border, background: "transparent", color: page >= totalPages - 1 ? T.textMuted : T.text, cursor: page >= totalPages - 1 ? "default" : "pointer", fontSize: 10, fontWeight: 700 }}>»</button>
            {/* Jump to page */}
            {totalPages > 7 && (
              <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: 8 }}>
                <span style={{ fontSize: 10, color: T.textMuted }}>Go:</span>
                <input value={jumpInput} onChange={e => setJumpInput(e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyDown={e => { if (e.key === "Enter" && jumpInput) { goPage(parseInt(jumpInput) - 1); setJumpInput(""); } }}
                  placeholder={String(page + 1)}
                  style={{ width: 40, padding: "3px 6px", borderRadius: 4, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11, outline: "none", textAlign: "center", fontFamily: "monospace" }} />
                <span style={{ fontSize: 9, color: T.textMuted }}>/ {totalPages}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── V2 SECTION HEADER ───
export const SectionHeader = ({ title, subtitle, action }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{title}</h1>
      {subtitle && <div style={{ fontSize: 13, color: T.textDim, marginTop: 4 }}>{subtitle}</div>}
    </div>
    {action}
  </div>
);

// ─── V2 CARD WRAPPER ───
export const Card = ({ children, style, accent }) => (
  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, position: "relative", overflow: "hidden", ...style }}>
    {accent && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${accent}, transparent)` }} />}
    {children}
  </div>
);

// ─── V2 TAB BAR ───
export const TabBar = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 20, background: T.bg, borderRadius: 8, padding: 3, border: `1px solid ${T.border}`, overflowX: "auto" }}>
    {tabs.map(t => (
      <button key={t.id} onClick={() => onChange(t.id)}
        style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: active === t.id ? T.cyanGlow : "transparent",
          color: active === t.id ? T.cyan : T.textDim, fontSize: 12, fontWeight: active === t.id ? 600 : 400,
          cursor: "pointer", whiteSpace: "nowrap" }}>
        {t.label} {t.count !== undefined && <span style={{ marginLeft: 4, opacity: 0.6 }}>({t.count})</span>}
      </button>
    ))}
  </div>
);

// ─── GROUP BADGE WITH POPUP ───
import { getGroupConfig } from '../riskConfig';

// Score bar mini component
function ScoreRow({ label, value, max, prev, color }) {
  const pct = max > 0 ? (value / max * 100) : 0;
  const prevPct = prev != null && max > 0 ? (prev / max * 100) : null;
  const delta = prev != null ? value - prev : null;
  return (
    <div style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: T.textMuted }}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {delta != null && delta !== 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, color: delta > 0 ? T.red : T.green }}>
              {delta > 0 ? "▲" : "▼"}{Math.abs(delta)}
            </span>
          )}
          <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{value}<span style={{ color: T.textMuted, fontWeight: 400 }}>/{max}</span></span>
        </div>
      </div>
      <div style={{ height: 4, background: T.bg, borderRadius: 2, position: "relative" }}>
        {prevPct != null && <div style={{ position: "absolute", height: "100%", width: prevPct + "%", background: T.textMuted + "40", borderRadius: 2 }} />}
        <div style={{ position: "relative", height: "100%", width: pct + "%", background: color || T.cyan, borderRadius: 2 }} />
      </div>
    </div>
  );
}

// Reasoning logic — dynamic based on group rules
function getGroupReasoning(client, gcfg) {
  if (!client || !client._groupResult) return null;
  const gr = client._groupResult;
  const th = gcfg.thresholds;
  const group = gr.group;
  const gDef = gcfg.groups[group];
  if (!gDef || !gDef.rules) return null;
  const rules = gDef.rules;
  const reasons = [];

  // Client type match
  if (rules.matchClientTypes?.length > 0) {
    const ct = String(client.ClientType || "").toLowerCase();
    if (rules.matchClientTypes.some(t => ct === t.toLowerCase())) {
      reasons.push({ text: `Client Type = "${client.ClientType}" matches [${rules.matchClientTypes.join(", ")}]`, pass: true });
      reasons.push({ text: `Type match always assigns to ${group} regardless of risk`, pass: true });
    }
    return { reasons };
  }

  // Condition-based reasoning
  const conds = rules.conditions || [];
  const isOr = rules.logic === "OR";

  conds.forEach(c => {
    switch (c.type) {
      case "minScore":
        const minPass = gr.score >= (c.value || 0);
        reasons.push({ text: `Risk Score ${gr.score} ${minPass ? "≥" : "<"} ${c.value}`, pass: minPass, critical: minPass && c.value >= 39 });
        break;
      case "maxScore":
        const maxPass = gr.score < (c.value || 0);
        reasons.push({ text: `Risk Score ${gr.score} ${maxPass ? "<" : "≥"} ${c.value}`, pass: maxPass });
        break;
      case "arbitrage":
        reasons.push({ text: gr.isArbitrage ? "Arbitrage Detected" : "No Arbitrage", pass: gr.isArbitrage, critical: gr.isArbitrage });
        break;
      case "minAbuseFlags":
        const afPass = gr.activeAbuseCount >= (c.value || 0);
        reasons.push({ text: `${gr.activeAbuseCount} abuse flags ${afPass ? "≥" : "<"} ${c.value} threshold`, pass: afPass, critical: afPass });
        break;
      case "profitable":
        reasons.push({ text: `Revenue ${gr.breakdown.revenue} ${gr.isProfitable ? "≥" : "<"} ${th.revenueForProfitable} (Profitable)`, pass: gr.isProfitable });
        break;
      case "notProfitable":
        reasons.push({ text: `Revenue ${gr.breakdown.revenue} ${!gr.isProfitable ? "<" : "≥"} ${th.revenueForProfitable} (Not Profitable)`, pass: !gr.isProfitable });
        break;
      case "clean":
        reasons.push({ text: `Compliance ${gr.breakdown.compliance} ${gr.isClean ? "<" : "≥"} ${th.complianceForClean} (Clean)`, pass: gr.isClean });
        break;
      case "notClean":
        reasons.push({ text: `Compliance ${gr.breakdown.compliance} ${!gr.isClean ? "≥" : "<"} ${th.complianceForClean} (Not Clean)`, pass: !gr.isClean });
        break;
      case "speedFlag":
        const sf = (th.speedFlags || []).filter(f => client[f] === f);
        reasons.push({ text: gr.hasSpeedFlag ? `Speed pattern: ${sf.join(", ")}` : "No speed flags", pass: gr.hasSpeedFlag });
        break;
      case "noSpeedFlag":
        reasons.push({ text: gr.hasSpeedFlag ? "Has speed flags (mismatch)" : "No speed abuse patterns", pass: !gr.hasSpeedFlag });
        break;
      case "default":
        reasons.push({ text: "Default catch-all — no higher-priority group matched", pass: true });
        break;
    }
  });

  if (isOr && conds.length > 1) {
    reasons.unshift({ text: `Logic: ANY condition is enough (OR)`, pass: true, info: true });
  }

  return { reasons };
}

export const GroupBadge = ({ group, size = "sm", client }) => {
  const [open, setOpen] = useState(false);
  const cfg = getGroupConfig();
  const g = cfg.groups[group];
  if (!g) return <span style={{ color: T.textMuted, fontSize: 10 }}>{group || "—"}</span>;
  const fs = size === "lg" ? 12 : 10;
  const reasoning = open ? getGroupReasoning(client, cfg) : null;
  const gr = client?._groupResult;
  const prevGr = client?._prevGroupResult;
  const changeReasons = client?._changeReasons;
  const hasChange = changeReasons && changeReasons.length > 0;

  return (
    <>
      <span onClick={e => { e.stopPropagation(); setOpen(true); }}
        style={{ display: "inline-block", padding: size === "lg" ? "3px 10px" : "2px 8px", borderRadius: 4, fontSize: fs, fontWeight: 700,
          color: g.color, background: g.color + "18", border: `1px solid ${g.color}30`, cursor: "pointer", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
        {group}
      </span>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", background: T.bgCard, border: `1px solid ${g.color}40`, borderRadius: 14, padding: 24, boxShadow: `0 20px 60px rgba(0,0,0,0.5)` }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: g.color }} />
                <span style={{ fontSize: 18, fontWeight: 700, color: g.color }}>{group}</span>
                {client?.Login && <span className="mono" style={{ fontSize: 11, color: T.textDim, background: T.bg, padding: "2px 8px", borderRadius: 4 }}>#{client.Login}</span>}
              </div>
              <span onClick={() => setOpen(false)} style={{ cursor: "pointer", color: T.textMuted, fontSize: 20, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, background: T.bg }}>×</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 2 }}>{g.name}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 16, lineHeight: 1.4 }}>{g.description}</div>

            {/* ── WHY THIS GROUP ── */}
            {reasoning && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.amber, marginBottom: 8, letterSpacing: 1 }}>Why This Group?</div>
                {reasoning.reasons.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 12px", marginBottom: 4,
                    background: r.critical ? T.red + "15" : r.pass ? T.green + "12" : T.bg,
                    borderRadius: 6, fontSize: 12, color: r.critical ? T.red : r.pass ? T.green : T.textDim,
                    borderLeft: `3px solid ${r.critical ? T.red : r.pass ? T.green : T.textMuted}40` }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{r.critical ? "⚠" : r.pass ? "✓" : "○"}</span>
                    <span>{r.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ── WHAT CHANGED (Group Changes context) ── */}
            {hasChange && (() => {
              const prevG = cfg.groups[client._prevGroup];
              const newG = cfg.groups[client._newGroup];
              const groupOrder = ["STD_B","STD_AB","PRO_A","SPD_A","HDG_A","B2B_A","CRT_A"];
              const escalated = groupOrder.indexOf(client._newGroup) > groupOrder.indexOf(client._prevGroup);
              return (
              <div style={{ marginBottom: 16 }}>
                {/* Recommendation box */}
                <div style={{ padding: 14, borderRadius: 10, marginBottom: 12, background: escalated ? T.red + "12" : T.green + "12", border: `1px solid ${escalated ? T.red : T.green}30` }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: escalated ? T.red : T.green, letterSpacing: 1, marginBottom: 8 }}>
                    {escalated ? "⚠ Escalation — Action Required" : "✓ De-escalation"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 700, color: prevG?.color || T.textMuted, background: (prevG?.color || T.textMuted) + "18", border: `1px solid ${prevG?.color || T.textMuted}30` }}>{client._prevGroup}</span>
                    <span style={{ color: T.textMuted, fontSize: 16 }}>→</span>
                    <span style={{ padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 700, color: newG?.color || T.textMuted, background: (newG?.color || T.textMuted) + "18", border: `1px solid ${newG?.color || T.textMuted}30` }}>{client._newGroup}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>Recommended: Move to <span style={{ color: newG?.color || T.text }}>{client._newGroup}</span></div>
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{newG?.name} — {newG?.description}</div>
                </div>

                {/* Change details */}
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.purple, letterSpacing: 1, marginBottom: 8 }}>What Changed?</div>
                {changeReasons.map((cr, i) => (
                  <div key={i} style={{ padding: "6px 12px", marginBottom: 4, background: T.bg, borderRadius: 6, fontSize: 12, borderLeft: `3px solid ${T.purple}40` }}>
                    {cr.from != null ? (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: T.textDim }}>{cr.field}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="mono" style={{ color: T.textMuted }}>{cr.from}/{cr.max}</span>
                          <span style={{ color: cr.to > cr.from ? T.red : T.green }}>→</span>
                          <span className="mono" style={{ color: cr.to > cr.from ? T.red : T.green, fontWeight: 700 }}>{cr.to}/{cr.max}</span>
                          <span style={{ fontSize: 10, color: cr.to > cr.from ? T.red : T.green, fontWeight: 700 }}>
                            ({cr.to > cr.from ? "+" : ""}{cr.to - cr.from})
                          </span>
                        </div>
                      </div>
                    ) : cr.flagsAdded ? (
                      <div><span style={{ color: T.red, fontWeight: 600 }}>+ New Flags: </span><span style={{ color: T.text }}>{cr.flagsAdded.join(", ")}</span></div>
                    ) : cr.flagsRemoved ? (
                      <div><span style={{ color: T.green, fontWeight: 600 }}>− Lost Flags: </span><span style={{ color: T.text }}>{cr.flagsRemoved.join(", ")}</span></div>
                    ) : cr.text ? (
                      <div><span style={{ color: T.textDim }}>{cr.field}: </span><span style={{ color: T.amber, fontWeight: 600 }}>{cr.text}</span></div>
                    ) : null}
                  </div>
                ))}
              </div>
              );
            })()}

            {/* ── SCORE BREAKDOWN ── */}
            {gr && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>
                  Score Breakdown
                  {prevGr && <span style={{ color: T.textDim, fontWeight: 400, textTransform: "none", marginLeft: 8 }}>(gray = previous month)</span>}
                </div>
                <div style={{ background: T.bg, borderRadius: 8, padding: 12 }}>
                  <ScoreRow label="Total Risk Score" value={gr.score} max={100} prev={prevGr?.score} color={gr.score >= 51 ? T.red : gr.score >= 39 ? T.orange : gr.score >= 27 ? T.amber : gr.score >= 16 ? T.cyan : T.green} />
                  <div style={{ height: 1, background: T.border + "30", margin: "6px 0" }} />
                  <ScoreRow label="Revenue" value={gr.breakdown.revenue} max={35} prev={prevGr?.breakdown.revenue} color={T.red} />
                  <ScoreRow label="Position" value={gr.breakdown.position} max={10} prev={prevGr?.breakdown.position} color={T.orange} />
                  <ScoreRow label="Compliance" value={gr.breakdown.compliance} max={30} prev={prevGr?.breakdown.compliance} color={T.amber} />
                  <ScoreRow label="Strategy" value={gr.breakdown.strategy} max={25} prev={prevGr?.breakdown.strategy} color={T.purple} />
                </div>
              </div>
            )}

            {/* ── FLAGS ── */}
            {gr && gr.activeFlags.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>Active Flags</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {gr.activeFlags.map((f, i) => {
                    const tc = { Scalper: T.amber, Hedger: T.green, HedgingAbuser: T.red, Commissioner: "#a855f7", NewsTrader: "#ec4899",
                      HighFrequencyTrader: T.orange, HeavyTrader: T.red, Swaper: T.blue, LongTrader: "#8b5cf6", GainerTrader: T.cyan };
                    const col = tc[f] || T.textDim;
                    return <span key={i} style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: col, background: col + "18", border: `1px solid ${col}30` }}>{f.replace(/([A-Z])/g, ' $1').trim()}</span>;
                  })}
                </div>
                {gr.activeAbuseCount > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: T.textMuted }}>
                    Abuse count: <span style={{ color: gr.activeAbuseCount >= 3 ? T.red : T.text, fontWeight: 700 }}>{gr.activeAbuseCount}</span>
                    {gr.isArbitrage && <span style={{ marginLeft: 10, color: "#ff0055", fontWeight: 700 }}>⚠ Arbitrage Suspected</span>}
                    {gr.isArbitrage && <span style={{ marginLeft: 10, color: T.red, fontWeight: 600 }}>Arbitrage Detected</span>}
                  </div>
                )}
              </div>
            )}

            {/* ── ACTIONS ── */}
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>Actions</div>
            {g.actions.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", marginBottom: 4, background: T.bg, borderRadius: 6, fontSize: 12, color: T.text }}>
                <span style={{ color: g.color, fontWeight: 700, fontSize: 11, minWidth: 16 }}>{i + 1}.</span> {a}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ─── HELP TIP (? tooltip that shows help content) ───
export const HelpTip = ({ id, onNavigate, size = 14 }) => {
  const [open, setOpen] = useState(false);
  const entry = HELP[id];
  if (!entry) return null;

  return (
    <>
      <span onClick={e => { e.stopPropagation(); setOpen(true); }}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, borderRadius: "50%", border: `1px solid ${T.textMuted}40`, color: T.textMuted, fontSize: size - 5, fontWeight: 700, cursor: "pointer", flexShrink: 0, lineHeight: 1, transition: "all 0.15s" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.cyan; e.currentTarget.style.color = T.cyan; e.currentTarget.style.background = T.cyan + "15"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = T.textMuted + "40"; e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = "transparent"; }}
        title={entry.short}>
        ?
      </span>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 420, maxHeight: "70vh", overflowY: "auto", background: T.bgCard, border: `1px solid ${T.cyan}30`, borderRadius: 14, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.cyan }}>{entry.title}</div>
              <span onClick={() => setOpen(false)} style={{ cursor: "pointer", color: T.textMuted, fontSize: 20, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, background: T.bg }}>×</span>
            </div>
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.6, marginBottom: entry.formula || entry.example ? 12 : 0 }}>{entry.short}</div>
            {entry.formula && (
              <div style={{ marginTop: 10, marginBottom: entry.example ? 10 : 0 }}>
                <div style={{ fontSize: 9, textTransform: "uppercase", fontWeight: 700, color: T.amber, letterSpacing: 1, marginBottom: 4 }}>Formula</div>
                <div style={{ background: T.bg, border: `1px solid ${T.amber}20`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 10, color: T.amber, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{entry.formula}</div>
              </div>
            )}
            {entry.example && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 9, textTransform: "uppercase", fontWeight: 700, color: T.green, letterSpacing: 1, marginBottom: 4 }}>Example</div>
                <div style={{ background: T.green + "08", border: `1px solid ${T.green}20`, borderRadius: 6, padding: "8px 12px", fontSize: 10, color: T.green, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{entry.example}</div>
              </div>
            )}
            {onNavigate && (
              <button onClick={() => { setOpen(false); onNavigate(id); }}
                style={{ marginTop: 12, padding: "6px 14px", borderRadius: 6, border: `1px solid ${T.cyan}30`, background: T.cyan + "10", color: T.cyan, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                Learn more →
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
